import type { ReactNode } from "react";
import { ModalOverlay, Modal, Dialog } from "@/components/application/modals/modal";
import { XClose } from "@untitledui/icons";

/**
 * AppDialog — tenký wrapper nad UUI react-aria modaly ve stylu Untitled UI:
 * fixní hlavička (title + supporting text + zavírací X), scrollovací tělo a
 * patička oddělená horním borderem s akcemi vpravo. Řízený přes `isOpen`/`onClose`.
 */
const SIZE_MAX_W = {
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-3xl",
  "2xl": "max-w-4xl",
  /** Pro inspekci logů — hlavičky a JSON těla se do užšího dialogu nevejdou. */
  "3xl": "max-w-6xl",
} as const;

export function AppDialog({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  isDismissable = true,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  size?: keyof typeof SIZE_MAX_W;
  /**
   * Zavření klikem vedle dialogu nebo Esc. Vypíná se u dialogů, kde běží dlouhá
   * operace a náhodné kliknutí by ji nechalo běžet naslepo (hromadné akce).
   * Křížek v hlavičce zůstává funkční vždy.
   */
  isDismissable?: boolean;
}) {
  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={(o) => !o && onClose()} isDismissable={isDismissable}>
      <Modal className={`w-full ${SIZE_MAX_W[size]}`}>
        <Dialog>
          <div className="flex max-h-[inherit] w-full flex-col">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-5">
              <div className="flex flex-col gap-1">
                <h2 className="text-lg font-semibold text-primary">{title}</h2>
                {description && <p className="text-sm text-tertiary">{description}</p>}
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="-mt-1 -mr-1 shrink-0 rounded-lg p-2 text-tertiary transition hover:bg-primary_hover hover:text-primary"
              >
                <XClose className="size-5" />
              </button>
            </div>

            {/* Body (scrolluje). Vnitřní obsah tady NEMÁ mít vlastní `max-h-*` obal —
                dvojitý strop dialog zbytečně zkrátí; Modal ho už omezuje na výšku
                viewportu a scrollování obstará tenhle prvek. */}
            {children && <div className="flex-1 overflow-y-auto px-6 pb-2">{children}</div>}

            {/* Footer */}
            {footer && (
              <div className="mt-1 flex justify-end gap-3 border-t border-secondary px-6 py-5">{footer}</div>
            )}
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
